import {
  BucketKind,
  IAnyEventFieldsFragment,
  IAnyTableEventFieldsFragment,
  IBasicEventValueFieldsFragment,
  ICohortFieldsFragment,
  IFieldFieldsFragment,
  IMeasureFieldsFragment,
  INamedBucketDefFieldsFragment,
  INamedFilterFieldsFragment,
  ISearchTagsQuery,
  ITagBranch,
  ITagFieldInlineBranch,
  ITagInlineBranch,
  ITagLeafSeparator,
  ITagNodeFieldsFragment,
  ITagOptionInput,
  ITagRoot,
  IVirtualEventFieldsFragment,
} from '../../../utils/apollo/graphqlSchema';
import { MaybeNil } from '../../../utils/interfaces';
import { CreatableProps } from 'react-select/creatable';
import { DraftFunction } from 'use-immer';
import { ChipLabelVariant } from '../../ChipLabels';

/**
 * Types related entity selection search and result payload data transformation.
 *
 * The payload of the query is flattened tree where tree nodes are listed in `nodes` array while parent-child
 * relationship is described in `mapping` array. This structure is efficient (duplicate nodes are listed only once)
 * and GraphQL friendly (it is nearly impossible to query arbitrary tree depth) but it is not convenient for traversing
 * and manipulation. We add a thin wrapped on "branch" (IBranchChildren) nodes to collect all children nodes so we
 * can traverse the tree by following plain-old JS references
 *
 * Using terminology from https://en.wikipedia.org/wiki/Tree_(data_structure)#Terminology
 *
 *   * An external node (also known as an outer node, **leaf node**, or terminal node) is any
 *   node that does not have child nodes.
 *   * An internal node (also known as an inner node, inode for short, or **branch node**)
 *   is any node of a tree that has child nodes.
 *
 * Choosing **branch** and **leaf** because they provide better mental correlation with biological trees
 */

export type ISearchTagPayload = ISearchTagsQuery['searchTags'];

// Extract the exact node types we query in GraphQL
// Uses TS helper to extract their types from the polymorphic ITagNodeFieldsFragment
type IPayloadPresentationNode = Extract<
  ITagNodeFieldsFragment,
  Pick<ITagLeafSeparator, '__typename'>
>;
type IPayloadBranchNode = Extract<
  ITagNodeFieldsFragment,
  Pick<ITagBranch, '__typename'>
>;
type IPayloadRootNode = Extract<
  ITagNodeFieldsFragment,
  Pick<ITagRoot, '__typename'>
>;
type IPayloadNestedFieldNode = Extract<
  ITagNodeFieldsFragment,
  | Pick<ITagInlineBranch, '__typename'>
  | Pick<ITagFieldInlineBranch, '__typename'>
>;
// the remaining nodes describe entities - thus using Exclude helper
export type IPayloadEntityNode = Exclude<
  ITagNodeFieldsFragment,
  | IPayloadPresentationNode
  | IPayloadBranchNode
  | IPayloadRootNode
  | IPayloadNestedFieldNode
>;

// Define the nodes the Entity Selection widget works with.
// Nodes are divided into 4 groups. Most of them can be reused from GraphQL with some exceptions

// Node Kind: Presentation - for example separators
export type IPresentationNode = IPayloadPresentationNode;
// Node Kind: Entity - all the rest nodes are considered entity (something that can be selected into the widget)
export type IEntityNode = IPayloadEntityNode;
// Node Kind: Branch - these are containers (think of them as folders, tags) of Any nodes (could recursively contain
// other Branch nodes
// It is augmented node with children references
export interface IWithChildren {
  children: IAnyNode[];
}

export type IBranchNode = IPayloadBranchNode & IWithChildren;
// Node Kind: Creatable - special node that UI injects when the widget allow creation of entities from the input
export interface ICreatableNode {
  __typename: '__CreatableNode';
}
// Node Kind: Inline Branch - special node displays its children within the panel, instead of folder-like
export type IInlineBranchNode = IPayloadNestedFieldNode & IWithChildren;

// The special node describing root of the tree
export type IRootNode = IWithChildren;

// Helper unions of different kind of nodes
export type ISelectableNode =
  | IBranchNode
  | IEntityNode
  | ICreatableNode
  | IInlineBranchNode;
export type IAnyNode = ISelectableNode | IPresentationNode;

export type ILoadableNode = IEntityNode | ICreatableNode | IInlineBranchNode;

// Describes the transformed payload into more convenient representation of the tree
export interface ISearchTagsState {
  root: IRootNode;
  defaultNode: MaybeNil<IEntityNode>;
}

/**
 * ISelectedEntity is union of all entities that the Entity Selection Widget supports: can accept as input
 * and produce a new selection as output
 */
export type ISelectedEntity =
  | ICohortFieldsFragment
  | IFieldFieldsFragment
  | IBasicEventValueFieldsFragment
  | IVirtualEventFieldsFragment
  | IAnyEventFieldsFragment
  | IAnyTableEventFieldsFragment
  | INamedFilterFieldsFragment
  | IMeasureFieldsFragment
  | INamedBucketDefFieldsFragment
  | ICustomBucketDef;

// special type to handle Custom Buckets: they are Basic Bucket entities but all they map to a single selection
// in the widget: either one of Value Binning, Date Binning, Enum Binning.
export interface ICustomBucketDef {
  __typename: '__CustomBucketDef';
  kind: BucketKind;
}

// Special internal entity to mark that the actual ISelectedEntity must be produced from function that is passed to the
// widget
export interface ICreatableEntity {
  __typename: '__CreatableEntity';
}

// Special internal entity
export interface INestedFieldEntity {
  __typename: '__NestedFieldEntity';
  field: IFieldFieldsFragment;
}

export interface INestedFieldState {
  key: MaybeNil<string>;
  type: MaybeNil<string>;
  error: MaybeNil<string>;
}

export interface IValidatedNestedField {
  fieldKey: string;
  key: string;
  type: string;
}

export type ILoadableEntity =
  | ISelectedEntity
  | ICreatableEntity
  | INestedFieldEntity;

// Helper type to narrow ISelectedEntity to a subset of all supported entities. Useful for more concrete typing
// at the usage side of the widget
export type ISubsetSelectedEntity<T extends ISelectedEntity['__typename']> =
  Extract<ISelectedEntity, { __typename: T }>;

export type ISubsetNode<T extends IAnyNode['__typename']> = Extract<
  IAnyNode,
  { __typename: T }
>;

/**
 * Types related expanding of the selected entity
 */

/**
 * Given the virtual event, produces a function which when called expands
 *  the virtual event (converts it to basic event(s))
 *
 *  The expansion of Virtual events depends on the Builder context, that's why it is function factory instead of
 *  plain function. The Factory can capture conditionals and either produce a function if the event can be expanded
 *  or undefined - if not
 *
 *  Returns undefined when expanding the virtual event is not allowed
 *  See: canExpandVirtualEvent function
 */
export type IExpandVirtualEventCallbackFactory = (
  virtualEvent: IVirtualEventFieldsFragment,
) => (() => void) | undefined;

interface IVirtualEventLabelUnionMember {
  type: 'VirtualEvent';
  onExpandFactory: IExpandVirtualEventCallbackFactory;
}

// Cohort expansion handler
export type IExpandCohortHandler = (cohort: ICohortFieldsFragment) => void;

interface ICohortLabelUnionMember {
  type: 'Cohort';
  onExpand: IExpandCohortHandler;
  isExpanded: boolean; // hides the expand button once expanded
}

// Named Filter expansion handler
export type IExpandNamedFilterHandler = (
  namedFilter: INamedFilterFieldsFragment,
) => void;

interface IFilterLabelUnionMember {
  type: 'Filter';
  onExpand: IExpandNamedFilterHandler;
}

// Measure Filter expansion handler
export type IExpandMeasureHandler = (measure: IMeasureFieldsFragment) => void;

interface IMeasureLabelUnionMember {
  type: 'Measure';
  onExpand: IExpandMeasureHandler;
}

interface IFieldLabelUnionMember {
  type: 'Field';
  chipLabelVariantPicker: (
    field: IFieldFieldsFragment,
  ) => ChipLabelVariant | undefined;
}

// Union of expansion handlers: since entity widget could support multiple entities, if they support expansion,
// the must be a handler passed to the widget to perform it
export type IEntitySelectionLabelProps =
  | IVirtualEventLabelUnionMember
  | ICohortLabelUnionMember
  | IFilterLabelUnionMember
  | IMeasureLabelUnionMember
  | IFieldLabelUnionMember;

/**
 * Types related to creating, selecting or removing expanding of the selected entity by the user
 */

// return true to close the panel after selecting the entity
export type ISelectEntityHandler = (entity: ISelectedEntity) => void;
export type IRemoveEntityHandler = (entity: ISelectedEntity) => void;
export type ICreatableEntityHandler = (
  text: string,
) => MaybeNil<ISelectedEntity>;
export type ICreateCustom = () => void;

type IReactSelectPassThroughProps = Pick<
  CreatableProps<ISelectedEntity, true, never>,
  'autoFocus' | 'placeholder' | 'className' | 'isDisabled'
>;

export interface IEntitySelectionProps extends IReactSelectPassThroughProps {
  selectedEntities: ISelectedEntity[];
  schemaId: string;
  tags: ITagOptionInput[];
  onSelectEntity?: ISelectEntityHandler;
  onRemoveEntity?: IRemoveEntityHandler;
  labelProps?: IEntitySelectionLabelProps[];
  onCreateEntity?: ICreatableEntityHandler;
  onCreateCustom?: ICreateCustom;
  onClose?: () => void;
  onOpen?: () => void;
  isError?: boolean;
  isReadOnly?: boolean;
  disabledClickHandler?: () => void;
  disabledEntities?: ISelectedEntity[];
}

export type IEntitySelectionContext = Pick<
  IEntitySelectionProps,
  'labelProps' | 'onRemoveEntity' | 'onClose'
>;

/**
 * The properties which entity selection panel requires
 */
export interface IEntitySelectionPanelProps {
  schemaId: IEntitySelectionProps['schemaId'];
  onSelectEntity: ((entity: ISelectedEntity) => void) | undefined;
  onCreateEntity: ((text: string) => void) | undefined;
  onCreateCustom: IEntitySelectionProps['onCreateCustom'];
  onClose: NonNullable<IEntitySelectionProps['onClose']>;
  selectedEntities: IEntitySelectionProps['selectedEntities'];
  disabledEntities: IEntitySelectionProps['disabledEntities'];
  tags: IEntitySelectionProps['tags'];
  searchTerm: string;
  setSearchTerm: (newSearchTerm: string) => void;
  setLoading: (loading: boolean) => void;
}

// Internal context for all components that work together to build the Panel
// Some of the properties of the panel are passed through but there are others that are internal to the panel
export interface IPanelStateContext
  extends Pick<
    IEntitySelectionPanelProps,
    | 'schemaId'
    | 'searchTerm'
    | 'selectedEntities'
    | 'tags'
    | 'onClose'
    | 'disabledEntities'
    | 'onCreateCustom'
  > {
  isCreatable: boolean;
  getEntityFromCache: (node: ILoadableNode) => MaybeNil<ILoadableEntity>;
  selectNodeHandler: (node: ILoadableNode) => void;
  getEntityFromCacheOrFetch: (node: ILoadableNode) => Promise<ILoadableEntity>;
  entityLoading: boolean;
  getNestedFieldState: (entity: INestedFieldEntity) => INestedFieldState;
  setNestedFieldState: (
    entity: INestedFieldEntity,
    draftFunction: DraftFunction<INestedFieldState>,
  ) => void;
  getValidatedNestedField: (
    entity: INestedFieldEntity,
  ) => IValidatedNestedField | null;
  toggleInlineBranch: (branch: IInlineBranchNode) => void;
  getIsExpandedInlineBranch: (branch: IInlineBranchNode) => boolean;
  refreshSearch: () => void;
}

// Different kind of (sub) Panels the widget can render
// Every vertical section (column) of the panel is Panel Column
export enum PanelColumnType {
  LIST = 1,
  DETAILS = 2,
  NO_RESULTS = 3,
}

export interface IPanelColumnList {
  index: number;
  type: PanelColumnType.LIST;
  items: IAnyNode[];
  selected: ISelectableNode;
  onSelectNode: (node: ISelectableNode) => void;
  shouldShowDataRefresh: boolean;
}

export interface IPanelColumnDetails {
  type: PanelColumnType.DETAILS;
  node: ILoadableNode | IInlineBranchNode;
}

export interface IPanelColumnNoResults {
  type: PanelColumnType.NO_RESULTS;
}

export type IPanelColumn =
  | IPanelColumnList
  | IPanelColumnDetails
  | IPanelColumnNoResults;
